import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { saveAs } from 'file-saver'
import Button from '@mui/material/Button'
import Popper from '@mui/material/Popper'
import Grow from '@mui/material/Grow'
import Paper from '@mui/material/Paper'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import MenuList from '@mui/material/MenuList'
import MenuItem from '@mui/material/MenuItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import IconButton from '@mui/material/IconButton'
import { useUploadFileMutation } from '@/api/whiteList'
import useFetchBlob from '@/hooks/useFetchBlob'
import { downloadFile } from '@/utils/downloadFile'
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { selectActiveBuildingId } from '@/features/building/buildingSlice'
import { useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import useLayout from '@/features/layout/useLayout'

const ImportExportButton = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const anchorRef = useRef(null);
  // const activeBuildingId = useSelector(selectActiveBuildingId);
  const [open, setOpen] = useState(false);
  const { downloadTemplate } = useHandleDownloadTemplate();
  const { handleDownload, isDownloading } = useHandleDownload();
  const {
    fileInputRef,
    handleUploadClick,
    handleFileChange,
    isUploading,
  } = useHandleUpload();
  const isLoading = isUploading || isDownloading;
  const { isMobile }  = useLayout();
  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        hidden
        accept=".xlsx,.xls"
        onChange={handleFileChange}
      />
      { 
        isMobile ? (
          <IconButton
            ref={anchorRef}
            disabled={isLoading}
            size="small"
            color="primary"
            onClick={() => setOpen(true)}
            >
              <InsertDriveFileOutlinedIcon />
          </IconButton>
        ) : (
          <Button
            ref={anchorRef}
            variant="outlined"
            size="small"
            color="primary"
            startIcon={<InsertDriveFileOutlinedIcon />}
            onClick={() => setOpen(true)}
            disabled={isLoading}
          >
            {t('btn_import-or-export_title')}
          </Button>
        )
      }
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        transition
        disablePortal
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === 'bottom-start' ? 'left top' : 'left bottom',
            }}
          >
            <Paper sx={{ border: '1px solid', borderColor: theme.palette.divider }}>
              <ClickAwayListener onClickAway={() => setOpen(false)}>
                <MenuList>
                  <MenuItem 
                    onClick={handleDownload}
                    disabled={isDownloading}
                  >
                    <ListItemIcon>
                      <DownloadIcon />
                    </ListItemIcon>
                    {t('download')}
                  </MenuItem>
                  <MenuItem 
                    onClick={downloadTemplate}
                    disabled={isDownloading}
                  >
                    <ListItemIcon>
                      <TextSnippetIcon />
                    </ListItemIcon>
                    {t('download_template')}
                  </MenuItem>
                  <MenuItem 
                    onClick={() => {
                      setOpen(false)
                      handleUploadClick()
                    }}
                    disabled={isUploading}
                  >
                    <ListItemIcon>
                      <UploadFileIcon />
                    </ListItemIcon>
                    {t('upload')}
                  </MenuItem>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  )
}

const useHandleUpload = () => {
  const activeBuildingId = useSelector(selectActiveBuildingId);
  const fileInputRef = useRef(/** @type {HTMLInputElement | null} */ (null));
  const [uploadFile, { 
    isLoading: isUploading, 
  }] = useUploadFileMutation();

  const handleUploadClick = () => {
    fileInputRef.current?.click()
  }

  /** @param {import('react').ChangeEvent<HTMLInputElement>} event */
  const handleFileChange = async (event) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file || !activeBuildingId) return

    const body = new FormData()
    body.append('file', file)
    try {
      await uploadFile({ id: activeBuildingId, body }).unwrap()
    } catch (err) {
      console.error(err)
    }
  }

  return {
    fileInputRef,
    handleUploadClick,
    handleFileChange,
    isUploading,
  };
}

const useHandleDownloadTemplate = () => {
  const downloadTemplate = async () => {
    try {
      const exceljsModule = await import('exceljs')
      const ExcelJS = exceljsModule.default ?? exceljsModule
      const workbook = new ExcelJS.Workbook()
      const sheet = workbook.addWorksheet('Template')
      sheet.addRow([
        'plate_number',
        'building_id',
        'comment',
        'expires_at',
        'phone_number',
      ])

      const wbout = await workbook.xlsx.writeBuffer()
      const blob = new Blob([wbout], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      })

      saveAs(blob, 'AiParking_Whitelist_Template.xlsx')
    } catch (err) {
      console.error('Ошибка при создании шаблона:', err)
      alert('Не удалось создать шаблон')
    }
  }
  return { downloadTemplate };
}

const useHandleDownload = () => {
  const activeBuildingId = useSelector(selectActiveBuildingId);
  const {
    refetch: triggerExport,
    isLoading: isDownloading,
    error: downloadError
  } = useFetchBlob(
    activeBuildingId ? `/api/v2/white_list/export/building/${activeBuildingId}` : '',
    { skip: true }
  );

  const handleDownload = async () => {
    try {
      const { blob, error } = await triggerExport()
      if (error || !blob) {
        throw error || new Error('Export error')
      }
      downloadFile(
        blob,
        `white_list_${activeBuildingId}.xlsx`,
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      )
    } catch (err) {
      console.error('Export error:', err)
    }
  }
  return {
    handleDownload,
    isDownloading,
    downloadError,
  }
}

export default ImportExportButton;